/**
 * AuthError.js - Custom error class for authentication-related errors
 * 
 * This class extends the standard Error class to provide more structured
 * error handling for authentication operations, including error codes
 * and the ability to capture original errors for debugging.
 */

class AuthError extends Error {
  /**
   * Create a new AuthError
   * 
   * @param {string} message - Human-readable error message
   * @param {string} code - Error code for programmatic handling
   * @param {Error|null} originalError - Original error that caused this error (if any)
   */
  constructor(message, code, originalError = null) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
    this.originalError = originalError;
    
    // Capture stack trace for better debugging
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AuthError);
    }
  }
  
  /**
   * Get a user-friendly message based on the error code
   * 
   * @returns {string} User-friendly error message
   */
  getUserMessage() {
    switch (this.code) {
      case 'AUTH_REFRESH_FAILED':
        return 'Unable to refresh authentication. Please try again later.';
      case 'AUTH_TOKEN_EXPIRED':
        return 'Your session has expired. Please log in again.';
      case 'AUTH_NETWORK_ERROR':
        return 'Network error occurred. Please check your connection and try again.';
      case 'AUTH_SERVER_ERROR':
        return 'Server error occurred. Our team has been notified.';
      default:
        return 'An authentication error occurred. Please try again.';
    }
  }
  
  /**
   * Log the error details to the console or error tracking service
   */
  logError() {
    console.error(`[AuthError] ${this.code}: ${this.message}`);
    if (this.originalError) {
      console.error('Original error:', this.originalError);
    }
    
    // Here you could also send to an error tracking service like Sentry
    // if (typeof window.errorTracker !== 'undefined') {
    //   window.errorTracker.captureException(this);
    // }
  }
}

export default AuthError;
